import { all, call, put, select, take } from "redux-saga/effects";
import { last } from "lodash";
import { saveAs } from "file-saver";
import { Intent } from "@blueprintjs/core";
import { generatePath } from "react-router-dom";
import { generateToasterMessageWithLink } from "components/Page/components/Toaster/utils";
import { RecipeScalingApiMethods } from "api/api-methods";
import BackendClient from "api/backend-client";
import { waitForLogin } from "store/auth/auth.saga";
import { parameterTypesSelector, processVariablesSelector } from "store/global/global.selectors";
import { makeUnsaved, setCurrentRequestTemplate, setDeleteTarget, setServerError, } from "store/knowledge-base-item/knowledge-base-item.actions";
import { currentItemIdentifierSelector, deleteTargetSelector, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { CALCULATION_ERROR_ID, RemovableEntityType, RequestTemplate, } from "store/knowledge-base-item/knowledge-base-item.types";
import logger from "utils/logger";
import Toaster from "components/Page/components/Toaster";
import { createEntityFromResultElementSourceSuccessful, createResultSetFailure, createResultSetSuccessful, deleteCalculationQueueElementFailure, deleteCalculationQueueElementSuccessful, deleteCalculationQueueRowFailure, deleteCalculationQueueRowSuccessful, deleteResultSetElementFailure, deleteResultSetElementSuccessful, deleteResultSetFailure, deleteResultSetSuccessful, duplicateResultSetElementFailure, duplicateResultSetElementSuccessful, duplicateResultSetFailure, duplicateResultSetSuccessful, editCalculationQueueElementFailure, editCalculationQueueElementSuccessful, editCalculationQueueFailure, editCalculationQueueRowFailure, editCalculationQueueRowSuccessful, editCalculationQueueSuccessful, exportResultSetElementFailure, exportResultSetElementSuccessful, getInitialPredictionTimeFailure, getLineGraphResultSuccessful, getProcessDataViewerResultSuccessful, getSimulationResult, getSimulationResultFailure, getSpaceviewerResultSuccessful, getTableResultSuccessful, moveCalculationElementFailure, moveCalculationElementRowFailure, moveCalculationElementRowSuccessful, moveCalculationElementSuccessful, moveResultSetFailure, moveResultSetSuccessful, orderResultSetElements, orderResultSetElementsFailure, orderResultSetElementsSuccessful, pauseCalculationFailure, pauseCalculationSuccessful, RsWorkspaceResultsActionType, setActiveResultElement, setLineGraphSourceParamsFailure, setLineGraphSourceParamsSuccessful, setPlotRangeConfigsFailure, setPlotRangeConfigsSuccessful, setPredictionTime, setProcessDataViewerSourceParamsFailure, setProcessDataViewerSourceParamsSuccessful, setResultIsFetched, setResultSets, setShouldScrollToRsResultElement, setSpaceviewerAxisFailure, setSpaceviewerAxisSuccessful, setSpaceviewerConfidenceFailure, setSpaceviewerConfidenceSuccessful, setSpaceviewerDensitySuccessful, setSpaceviewerSourceViewFailure, startCalculationFailure, startCalculationSuccessful, stopCalculationFailure, stopCalculationSuccessful, updateCalculationStatus, updatePlotSourceConfigsFailure, updateResultSetElementFailure, updateResultSetElementSuccessful, updateResultSetFailure, updateResultSetSuccessful, updateTableElementViewFailure, updateTableElementViewSuccessful, upsertResultElementFailure, upsertResultElementSuccessful, } from "../actions/results.actions";
import { CalculationStatusSource, EntitiesCreatableFromResultElement, ResultSetElementType, SimulationRunState, } from "../rs-workspace.types";
import { activeResultElementSelector, duplicateTargetResultSetIdSelector, getActiveResultSetElements, getActiveResultSetIdSelector, getActiveResultSetSelector, getResultSetElementByIdSelector, getResultSetsSelector, isResultFetchingSelector, reFetchedResultsScenarioEntityUsedInSelector, resultElementExportedDataSelector, resultSetByIdSelector, } from "../selectors/results.selectors";
import { incompleteSimulationsIdsSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "../selectors/rs-workspace.selectors";
import { getNewElementPosition, rearrangeResultSets } from "../utils/results.utils";
import { isResultElementCompleted } from "../utils/completeness.utils";
import { getEmptySpaceviewerResultSource } from "../utils/common.utils";
import { isResultElementCompletedSelector } from "../selectors/completeness.selectors";
import { setScenario } from "../actions/rs-workspace.actions";
import { isNotEmpty } from "utils/validate";
import { apiErrorHandler } from "store/global/global.saga";
import RoutePaths from "routes/route-paths";
import { getCreateEntityRequestTemplate } from "pages/RS-Workspace/components/Results/components/CreateEntityFromResultElementDialog/utils";
export function* getAllSimulationResultsSaga(workspaceId, resultSets) {
    try {
        const insights = yield select(workspaceInsightsSelector);
        const simulations = yield select(workspaceSimulationsSelector);
        const processVariables = yield select(processVariablesSelector);
        const cellModels = yield select(workspaceCellModelsSelector);
        const recipes = yield select(workspaceRecipesSelector);
        const optimizations = yield select(workspaceOptimizationsSelector);
        const incompleteSimulationsIds = yield select(incompleteSimulationsIdsSelector);
        yield all(resultSets.reduce((acc, { id: resultSetId, elements }) => {
            elements.forEach((element) => {
                if (isResultElementCompleted(element, cellModels, recipes, simulations, insights, processVariables, incompleteSimulationsIds, optimizations)) {
                    acc.push(put(getSimulationResult({ workspaceId, resultSetId, elementId: element.id, preload: true })));
                }
            });
            return acc;
        }, []));
    }
    catch (e) {
        logger.error("get all table elements results");
    }
}
export function* rsWorkspaceGetRelatedToScenarioEntityResultsSaga(entityType, entityId) {
    try {
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const resultsToReFetch = yield select(reFetchedResultsScenarioEntityUsedInSelector(entityType, entityId));
        const payload = resultsToReFetch.map((item) => ({ ...item, workspaceId: Number(workspaceId) }));
        for (const result of payload) {
            yield put(getSimulationResult(result));
        }
    }
    catch (e) {
        logger.error("workspace get related to scenario entity results failed");
    }
}
export function* createResultSetSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.CreateResultSet], payload);
        yield put(setResultSets(data.resultSets));
        yield put(createResultSetSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add result set failed");
        yield put(createResultSetFailure());
    }
}
export function* moveResultSetSaga({ payload: { workspaceId, elementId, sourceIndex, destinationIndex }, }) {
    const resultSets = yield select(getResultSetsSelector);
    const resultSetsRearranged = yield call(rearrangeResultSets, resultSets, sourceIndex, destinationIndex);
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const payload = {
            workspaceId,
            elementId,
            previousElementId: resultSetsRearranged[destinationIndex - 1]?.id ?? 0,
        };
        // optimistic save
        yield put(setResultSets(resultSetsRearranged));
        yield call([backendClient, RecipeScalingApiMethods.MoveResultSet], payload);
        yield put(moveResultSetSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace move result set failed");
        // save initial value
        yield put(setResultSets(resultSets));
        yield put(moveResultSetFailure());
    }
}
function* createResultSetElementSaga(payload) {
    const { id: workspaceId } = yield select(currentItemIdentifierSelector);
    const elements = yield select(getActiveResultSetElements);
    const backendClient = BackendClient.getInstance();
    let template;
    let method;
    switch (payload.type) {
        case ResultSetElementType.Table:
            template = RequestTemplate.TABLE_ELEMENT_POST;
            method = RecipeScalingApiMethods.CreateTableElement;
            break;
        case ResultSetElementType.SpaceViewer:
            template = RequestTemplate.SPACEVIEWER_ELEMENT_POST;
            method = RecipeScalingApiMethods.CreateSpaceViewerElement;
            break;
        case ResultSetElementType.ProcessDataViewer:
            template = RequestTemplate.PROCESS_DATA_VIEWER_ELEMENT_POST;
            method = RecipeScalingApiMethods.CreateProcessDataViewerElement;
            break;
        case ResultSetElementType.LineGraph:
            template = RequestTemplate.LINE_GRAPH_ELEMENT_POST;
            method = RecipeScalingApiMethods.CreateLineGraphElement;
            break;
    }
    if (template && method) {
        yield put(setCurrentRequestTemplate(template));
        const data = yield call([backendClient, method], {
            ...payload,
            workspaceId,
        });
        const position = getNewElementPosition(data, elements);
        if (elements.length > 0 && (data.row !== position.row || data.column !== position.column)) {
            yield put(orderResultSetElements({
                workspaceId,
                resultSetId: payload.resultSetId,
                elements: [
                    ...elements.map(({ id, row, column }) => ({ id, row, column })),
                    { id: data.id, ...position },
                ],
            }));
        }
        yield put(upsertResultElementSuccessful({
            resultSetId: payload.resultSetId,
            ...data,
            ...position,
        }));
        yield put(setShouldScrollToRsResultElement(true));
        yield put(setActiveResultElement(null));
        const isDataCompleted = yield select(isResultElementCompletedSelector(data, true));
        if (isDataCompleted) {
            yield put(getSimulationResult({ workspaceId, resultSetId: payload.resultSetId, elementId: data.id }));
        }
    }
}
function* editResultSetElementSaga(payload) {
    const { id: workspaceId } = yield select(currentItemIdentifierSelector);
    const backendClient = BackendClient.getInstance();
    let template;
    let method;
    switch (payload.type) {
        case ResultSetElementType.Table:
            template = RequestTemplate.TABLE_ELEMENT_PUT;
            method = RecipeScalingApiMethods.UpdateTableElement;
            break;
        case ResultSetElementType.SpaceViewer:
            template = RequestTemplate.SPACEVIEWER_ELEMENT_PUT;
            method = RecipeScalingApiMethods.UpdateSpaceViewerElement;
            break;
        case ResultSetElementType.ProcessDataViewer:
            template = RequestTemplate.PROCESS_DATA_VIEWER_ELEMENT_PUT;
            method = RecipeScalingApiMethods.UpdateProcessDataViewerElement;
            break;
        case ResultSetElementType.LineGraph:
            template = RequestTemplate.LINE_GRAPH_ELEMENT_PUT;
            method = RecipeScalingApiMethods.UpdateLineGraphElement;
            break;
    }
    if (template && method) {
        yield put(setCurrentRequestTemplate(template));
        const data = yield call([backendClient, method], {
            ...payload,
            workspaceId,
        });
        yield put(upsertResultElementSuccessful({ resultSetId: payload.resultSetId, ...data }));
        yield put(setActiveResultElement(null));
        const isDataCompleted = yield select(isResultElementCompletedSelector(data, true));
        yield put(getSimulationResult({
            workspaceId,
            resultSetId: payload.resultSetId,
            elementId: payload.elementId,
            reset: !isDataCompleted,
        }));
    }
}
export function* upsertResultElementSaga({ payload, }) {
    try {
        yield call(waitForLogin);
        yield put(makeUnsaved());
        yield call(payload.elementId === -1 ? createResultSetElementSaga : editResultSetElementSaga, payload);
    }
    catch (e) {
        yield call(apiErrorHandler, e, `${payload.elementId === -1 ? "create" : "edit"} ${payload.type} element failed`);
        yield put(upsertResultElementFailure());
    }
}
export function* updateTableElementViewSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            yield put(makeUnsaved());
            const { id: workspaceId } = yield select(currentItemIdentifierSelector);
            const data = yield call([backendClient, RecipeScalingApiMethods.UpdateTableElementView], {
                ...payload,
                workspaceId,
            });
            yield put(updateTableElementViewSuccessful({ resultSetId: payload.resultSetId, ...data }));
        }
        else {
            const { resultSetId, elementId, items } = payload;
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const columns = resultElement.columns.map((column) => {
                const payloadItem = items.find(({ id }) => column.id === id);
                return payloadItem ? { ...column, view: payloadItem.viewType } : column;
            });
            yield put(updateTableElementViewSuccessful({ resultSetId, ...resultElement, columns }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update table element view failed");
        yield put(updateTableElementViewFailure());
    }
}
export function* updateResultSetSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.RESULT_SET_PUT));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.UpdateResultSet], payload);
        yield put(setServerError(null));
        yield put(setResultSets(data.resultSets));
        yield put(updateResultSetSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update result set failed");
        yield put(updateResultSetFailure());
    }
}
export function* deleteResultSetSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const { id: resultSetId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.RESULT_SET) {
            return;
        }
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteResultSet], { workspaceId, resultSetId });
        yield put(setServerError(null));
        yield put(setScenario(data));
        yield put(deleteResultSetSuccessful(resultSetId));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete result set failed");
        yield put(deleteResultSetFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
export function* updateResultSetElementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        yield put(setCurrentRequestTemplate(RequestTemplate.RESULT_SET_ELEMENT_PUT));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.UpdateResultSetElement], { ...payload, workspaceId });
        yield put(updateResultSetElementSuccessful({ ...data, resultSetId: payload.resultSetId }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update result set element failed", payload.elementId);
        yield put(updateResultSetElementFailure());
    }
}
export function* deleteResultSetElementSaga() {
    try {
        yield call(waitForLogin);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const { id: elementId, additionalKeys: resultSetId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.RESULT_SET_ELEMENT) {
            return;
        }
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteResultSetElement], { resultSetId, elementId, workspaceId });
        yield put(setServerError(null));
        yield put(setScenario(data));
        yield put(deleteResultSetElementSuccessful({ resultSetElementId: elementId, resultSetId }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete result set element failed");
        yield put(deleteResultSetElementFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
export function* duplicateResultSetElementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const { resultSetId, elementId } = yield select(activeResultElementSelector);
        const targetResultSetId = yield select(duplicateTargetResultSetIdSelector);
        const targetResultSet = yield select(resultSetByIdSelector(targetResultSetId));
        const targetResultSetElements = targetResultSet?.elements;
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setCurrentRequestTemplate(RequestTemplate.RESULT_SET_ELEMENT_DUPLICATE));
        const elementPosition = getNewElementPosition(payload, targetResultSetElements);
        const data = yield call([backendClient, RecipeScalingApiMethods.DuplicateResultSetElement], {
            workspaceId,
            resultSetId,
            elementId,
            targetResultSetId: targetResultSetId ?? 0,
            element: { ...payload, ...elementPosition },
        });
        yield put(setServerError(null));
        yield put(setScenario(data));
        yield put(duplicateResultSetElementSuccessful());
        const updatedResultSet = data.resultSets.find(({ id }) => id === targetResultSetId);
        if (updatedResultSet) {
            const duplicatedResultElement = last(updatedResultSet.elements);
            if (duplicatedResultElement) {
                yield put(getSimulationResult({
                    workspaceId,
                    resultSetId: targetResultSetId ?? resultSetId,
                    elementId: duplicatedResultElement.id,
                }));
            }
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "duplicate result set element failed");
        yield put(duplicateResultSetElementFailure());
    }
}
export function* duplicateResultSetSaga() {
    try {
        yield call(waitForLogin);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const activeResultSet = yield select(getActiveResultSetSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.DuplicateResultSet], {
            workspaceId,
            resultSetId: activeResultSet?.id,
        });
        yield put(setServerError(null));
        yield put(setScenario(data));
        yield put(duplicateResultSetSuccessful(data));
        const duplicatedResultSet = last(data.resultSets);
        if (duplicatedResultSet?.elements) {
            for (const duplicatedElement of duplicatedResultSet?.elements) {
                yield put(getSimulationResult({
                    workspaceId,
                    resultSetId: duplicatedResultSet.id,
                    elementId: duplicatedElement.id,
                }));
            }
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "duplicate result set failed");
        yield put(duplicateResultSetFailure());
    }
}
export function* orderResultSetElementsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.OrderResultSetElements], payload);
        yield put(setResultSets(data.resultSets));
        yield put(orderResultSetElementsSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update result set element failed");
        yield put(orderResultSetElementsFailure());
    }
}
export function* setSpaceviewerAxisSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const { resultSetId, elementId, xAxisId, yAxisId } = payload;
        const editMode = yield select(isEditableSelector);
        if (editMode) {
            const { id: workspaceId } = yield select(currentItemIdentifierSelector);
            yield put(makeUnsaved());
            const backendClient = BackendClient.getInstance();
            yield call([backendClient, RecipeScalingApiMethods.SetSpaceviewerAxis], {
                axis: "x",
                resultSetId,
                elementId,
                workspaceId,
                axisId: xAxisId,
            });
            const data = yield call([backendClient, RecipeScalingApiMethods.SetSpaceviewerAxis], {
                axis: "y",
                resultSetId,
                elementId,
                workspaceId,
                axisId: yAxisId,
            });
            yield put(setSpaceviewerAxisSuccessful({ resultSetId, ...data }));
        }
        else {
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            yield put(setSpaceviewerAxisSuccessful({ resultSetId, ...resultElement, xAxisId, yAxisId }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set space viewer axis failed");
        yield put(setSpaceviewerAxisFailure());
    }
}
export function* setSpaceviewerSourceViewSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const { resultSetId, elementId, id } = payload;
        let isSpaceviewerDataCompleted = false;
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            yield put(makeUnsaved());
            const data = yield call([backendClient, RecipeScalingApiMethods.SetSpaceviewerSourceView], { ...payload, workspaceId });
            yield put(upsertResultElementSuccessful({ resultSetId, ...data }));
            isSpaceviewerDataCompleted = yield select(isResultElementCompletedSelector(data, true));
        }
        else {
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const sources = resultElement.sources.map((source) => ({ ...source, view: source.id === id }));
            yield put(upsertResultElementSuccessful({ resultSetId, ...resultElement, sources }));
            isSpaceviewerDataCompleted = yield select(isResultElementCompletedSelector({ ...resultElement, sources }, true));
        }
        if (isSpaceviewerDataCompleted) {
            yield put(getSimulationResult({ workspaceId, resultSetId, elementId }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set space viewer source to view failed");
        yield put(setSpaceviewerSourceViewFailure());
    }
}
export function* setSpaceviewerConfidenceSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            const { id: workspaceId } = yield select(currentItemIdentifierSelector);
            yield put(makeUnsaved());
            const data = yield call([backendClient, RecipeScalingApiMethods.SetSpaceviewerConfidence], { ...payload, workspaceId });
            yield put(setSpaceviewerConfidenceSuccessful({ resultSetId: payload.resultSetId, ...data }));
        }
        else {
            const { resultSetId, elementId, min, max, sourceId } = payload;
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const sources = resultElement.sources.map((source) => source.id === sourceId ? { ...source, confidenceMax: max, confidenceMin: min } : source);
            yield put(setSpaceviewerConfidenceSuccessful({ resultSetId, ...resultElement, sources }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set space viewer confidence failed");
        yield put(setSpaceviewerConfidenceFailure());
    }
}
export function* setSpaceviewerDensitySaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            const { id: workspaceId } = yield select(currentItemIdentifierSelector);
            yield put(makeUnsaved());
            const data = yield call([backendClient, RecipeScalingApiMethods.SetSpaceviewerDensity], { ...payload, workspaceId });
            yield put(setSpaceviewerDensitySuccessful({ resultSetId: payload.resultSetId, ...data }));
        }
        else {
            const { resultSetId, elementId, density, sourceId } = payload;
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const sources = resultElement.sources.map((source) => source.id === sourceId ? { ...source, density: density } : source);
            yield put(setSpaceviewerConfidenceSuccessful({ resultSetId, ...resultElement, sources }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set space viewer density failed");
        yield put(setSpaceviewerConfidenceFailure());
    }
}
export function* setProcessDataViewerSourceParamsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        const { resultSetId, elementId, sourceId, ...fields } = payload;
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            yield put(makeUnsaved());
            const data = yield call([backendClient, RecipeScalingApiMethods.SetProcessDataViewerSourceParams], { ...payload, workspaceId });
            yield put(setProcessDataViewerSourceParamsSuccessful({ resultSetId, ...data }));
        }
        else {
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const sources = resultElement.sources?.map((source) => source.id === sourceId ? { ...source, ...fields } : source) || null;
            yield put(setProcessDataViewerSourceParamsSuccessful({ resultSetId, ...resultElement, sources }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set process data viewer source params failed");
        yield put(setProcessDataViewerSourceParamsFailure());
    }
}
export function* setLineGraphSourceParamsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const editMode = yield select(isEditableSelector);
        const { resultSetId, elementId, sourceId, ...fields } = payload;
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        if (editMode) {
            const backendClient = BackendClient.getInstance();
            yield put(makeUnsaved());
            const data = yield call([backendClient, RecipeScalingApiMethods.SetLineGraphSourceParams], { ...payload, workspaceId });
            yield put(setLineGraphSourceParamsSuccessful({ resultSetId, ...data }));
        }
        else {
            const resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
            if (!resultElement) {
                return;
            }
            const sources = resultElement.sources?.map((source) => source.id === sourceId ? { ...source, ...fields } : source) || null;
            yield put(setLineGraphSourceParamsSuccessful({ resultSetId, ...resultElement, sources }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "set line graph source params failed");
        yield put(setLineGraphSourceParamsFailure());
    }
}
function* updateCalculationStatusSaga(workspaceId, editMode) {
    const backendClient = BackendClient.getInstance();
    const calculation = yield call([backendClient, RecipeScalingApiMethods.GetCalculationStatus], workspaceId, editMode);
    yield put(updateCalculationStatus(calculation));
}
export function* getInitialPredictionTimeSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetCalculationPredictions], payload);
        yield put(setPredictionTime(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get calculation predictions failed");
        yield put(getInitialPredictionTimeFailure());
    }
}
export function* getSimulationResultSaga({ payload, }) {
    let type;
    if (payload.reset) {
        yield put(setResultIsFetched());
        return;
    }
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const editMode = yield select(isEditableSelector);
        const { saved } = yield select(currentItemIdentifierSelector);
        const element = yield select(getResultSetElementByIdSelector(payload.resultSetId, payload.elementId));
        const isCompleted = yield select(isResultElementCompletedSelector(element, true));
        if (!element) {
            return;
        }
        type = element.type;
        const temporary = editMode ? !saved : false;
        switch (type) {
            case ResultSetElementType.Table:
                {
                    let data = {};
                    if (isCompleted) {
                        data = yield call([backendClient, RecipeScalingApiMethods.GetSimulationResult], {
                            ...payload,
                            temporary,
                        });
                    }
                    else {
                        data = {
                            elementType: ResultSetElementType.Table,
                            rows: [],
                        };
                    }
                    yield put(getTableResultSuccessful({
                        ...data,
                        resultSetId: payload.resultSetId,
                        resultSetElementId: payload.elementId,
                    }));
                }
                break;
            case ResultSetElementType.SpaceViewer:
                {
                    let sources = [];
                    if (isCompleted) {
                        const sourceId = element.sources.find(({ view }) => !!view)?.id;
                        if (sourceId === undefined) {
                            throw new Error();
                        }
                        sources = yield call([backendClient, RecipeScalingApiMethods.GetSpaceviewerResult], {
                            ...payload,
                            temporary,
                            sourceId,
                        });
                    }
                    else {
                        sources = (element.sources ?? []).map(({ id }) => getEmptySpaceviewerResultSource(id));
                    }
                    yield put(getSpaceviewerResultSuccessful({
                        sources,
                        resultSetId: payload.resultSetId,
                        resultSetElementId: payload.elementId,
                    }));
                }
                break;
            case ResultSetElementType.ProcessDataViewer:
            case ResultSetElementType.LineGraph: {
                let results = [];
                if (isCompleted) {
                    const method = type === ResultSetElementType.ProcessDataViewer
                        ? RecipeScalingApiMethods.GetProcessDataViewerResult
                        : RecipeScalingApiMethods.GetLineGraphResult;
                    results = yield call([backendClient, method], { ...payload, temporary });
                }
                else {
                    results = (element.sources ?? []).map((source) => ({
                        elementType: type,
                        state: SimulationRunState.UNKNOWN,
                        sourceId: source.id ?? 0,
                        points: [],
                        errors: [],
                        hasErrors: false,
                        fetching: false,
                        accuracy: null,
                    }));
                }
                const successMethod = type === ResultSetElementType.ProcessDataViewer
                    ? getProcessDataViewerResultSuccessful
                    : getLineGraphResultSuccessful;
                const processVariables = yield select(processVariablesSelector);
                const parameterTypes = yield select(parameterTypesSelector);
                yield put(successMethod({
                    results,
                    resultSetId: payload.resultSetId,
                    resultSetElementId: payload.elementId,
                    processVariables,
                    parameterTypes,
                }));
            }
        }
        if (!payload.preload && editMode) {
            yield call(updateCalculationStatusSaga, payload.workspaceId, temporary);
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get simulation result failed");
        yield put(getSimulationResultFailure({ ...payload, type }));
    }
    finally {
        yield put(setResultIsFetched());
    }
}
export function* editCalculationQueueSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.EditCalculationQueue], id, payload);
        yield put(editCalculationQueueSuccessful(calculationState));
        for (const calculationElement of calculationState.calculation.elements) {
            if (calculationElement.rows.some(({ status }) => status === CalculationStatusSource.Completed)) {
                yield put(getSimulationResult({
                    workspaceId: id,
                    resultSetId: calculationElement.resultSetId,
                    elementId: calculationElement.resultElementId,
                }));
            }
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "calculation queue editing is failed", CALCULATION_ERROR_ID);
        yield put(editCalculationQueueFailure());
    }
}
export function* editCalculationQueueRowSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const { rowId, ...request } = payload;
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.EditCalculationQueueRow], id, rowId, request);
        yield put(editCalculationQueueRowSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "calculation queue row editing is failed", CALCULATION_ERROR_ID);
        yield put(editCalculationQueueRowFailure());
    }
}
export function* editCalculationQueueElementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const { field, ...request } = payload;
        let method;
        switch (field) {
            case "variability":
                method = RecipeScalingApiMethods.EditCalculationRowsVariability;
                break;
            case "uncertainty":
                method = RecipeScalingApiMethods.EditCalculationRowsUncertainty;
                break;
            case "numberOfOptimisationSteps":
                method = RecipeScalingApiMethods.EditCalculationRowsSteps;
                break;
        }
        if (method) {
            const calculationState = yield call([backendClient, method], id, request);
            yield put(editCalculationQueueElementSuccessful(calculationState));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "calculation queue element editing is failed", CALCULATION_ERROR_ID);
        yield put(editCalculationQueueElementFailure());
    }
}
export function* deleteCalculationQueueRowSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.DeleteCalculationQueueRow], id, payload);
        yield put(deleteCalculationQueueRowSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete calculation queue is failed", CALCULATION_ERROR_ID);
        yield put(deleteCalculationQueueRowFailure());
    }
}
export function* deleteCalculationQueueElementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.DeleteCalculationQueueElement], id, payload);
        yield put(deleteCalculationQueueElementSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete calculation queue element is failed", CALCULATION_ERROR_ID);
        yield put(deleteCalculationQueueElementFailure());
    }
}
export function* moveCalculationQueueElementSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.MoveCalculationQueueElement], id, payload);
        yield put(moveCalculationElementSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "move calculation queue element is failed", CALCULATION_ERROR_ID);
        yield put(moveCalculationElementFailure());
    }
}
export function* moveCalculationQueueElementRowSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        yield put(setServerError(null));
        const { id } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.MoveCalculationQueueElementRow], id, payload);
        yield put(moveCalculationElementRowSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "move calculation queue element row is failed", CALCULATION_ERROR_ID);
        yield put(moveCalculationElementRowFailure());
    }
}
export function* startCalculationSaga() {
    try {
        yield call(waitForLogin);
        yield put(setServerError(null));
        const backendClient = BackendClient.getInstance();
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.StartCalculation], workspaceId);
        yield put(startCalculationSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "start calculation queue is failed", CALCULATION_ERROR_ID);
        yield put(startCalculationFailure());
    }
}
export function* pauseCalculationSaga() {
    try {
        yield call(waitForLogin);
        yield put(setServerError(null));
        const backendClient = BackendClient.getInstance();
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.PauseCalculation], workspaceId);
        yield put(pauseCalculationSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "pause calculation queue is failed", CALCULATION_ERROR_ID);
        yield put(pauseCalculationFailure());
    }
}
export function* stopCalculationSaga() {
    try {
        yield call(waitForLogin);
        yield put(setServerError(null));
        const backendClient = BackendClient.getInstance();
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const calculationState = yield call([backendClient, RecipeScalingApiMethods.CancelCalculation], workspaceId);
        yield put(stopCalculationSuccessful(calculationState));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "stop calculation queue is failed", CALCULATION_ERROR_ID);
        yield put(stopCalculationFailure());
    }
}
export function* handleCalculationStatusChangeSaga() {
    const editMode = yield select(isEditableSelector);
    const { saved, id } = yield select(currentItemIdentifierSelector);
    const temporary = editMode ? !saved : false;
    const resultIsFetching = yield select(isResultFetchingSelector);
    if (resultIsFetching) {
        yield take(RsWorkspaceResultsActionType.SET_RESULT_IS_FETCHED);
    }
    yield call(updateCalculationStatusSaga, id, temporary);
}
export function* exportResultElementSaga({ payload }) {
    try {
        const resultSetId = yield select(getActiveResultSetIdSelector);
        const { csvData, fileName } = yield select(resultElementExportedDataSelector(resultSetId, payload));
        if (!csvData) {
            throw new Error();
        }
        const fileToSave = new Blob([csvData], { type: "csv" });
        saveAs(fileToSave, `${fileName}.csv`);
    }
    catch (e) {
        yield put(exportResultSetElementFailure());
        logger.error("export result element failed");
    }
    finally {
        yield put(exportResultSetElementSuccessful());
    }
}
export function* createEntityFromResultElementSourceSaga({ payload, }) {
    try {
        const backendClient = BackendClient.getInstance();
        const resultSetId = yield select(getActiveResultSetIdSelector);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const isCellModel = payload.entityType === EntitiesCreatableFromResultElement.CellModel;
        const isVariant = payload.entityType === EntitiesCreatableFromResultElement.Variant;
        yield call(waitForLogin);
        let method;
        const requestTemplate = getCreateEntityRequestTemplate(payload.resultElementType, payload.entityType);
        switch (payload.resultElementType) {
            case ResultSetElementType.Table:
                if (isVariant) {
                    method = RecipeScalingApiMethods.CreateVariantFromTableRow;
                    break;
                }
                method = isCellModel
                    ? RecipeScalingApiMethods.CreateCellModelFromTableRow
                    : RecipeScalingApiMethods.CreateRecipeFromTableRow;
                break;
            case ResultSetElementType.ProcessDataViewer:
                if (isVariant) {
                    method = RecipeScalingApiMethods.CreateVariantFromProcessDataViewerSource;
                    break;
                }
                method = isCellModel
                    ? RecipeScalingApiMethods.CreateCellModelFromProcessDataViewerSource
                    : RecipeScalingApiMethods.CreateRecipeFromProcessDataViewerSource;
                break;
            case ResultSetElementType.SpaceViewer:
                if (isVariant) {
                    method = RecipeScalingApiMethods.CreateVariantFromSpaceViewerSource;
                    break;
                }
                method = isCellModel
                    ? RecipeScalingApiMethods.CreateCellModelFromSpaceViewerSource
                    : RecipeScalingApiMethods.CreateRecipeFromSpaceViewerSource;
                break;
            case ResultSetElementType.LineGraph:
                if (isVariant) {
                    method = RecipeScalingApiMethods.CreateVariantFromLineGraphSource;
                    break;
                }
                method = isCellModel
                    ? RecipeScalingApiMethods.CreateCellModelFromLineGraphSource
                    : RecipeScalingApiMethods.CreateRecipeFromLineGraphSource;
                break;
            default:
                break;
        }
        if (!method || !requestTemplate) {
            return;
        }
        yield put(setCurrentRequestTemplate(requestTemplate));
        const entityId = yield call([backendClient, method], {
            workspaceId,
            resultSetId,
            ...payload,
        });
        const entityHumanName = isCellModel ? "Cell model" : "Recipe";
        const regularMessage = generateToasterMessageWithLink(`${entityHumanName} ${payload.data.name} has been successfully created.`, `Click here to navigate to the ${entityHumanName}.`, generatePath(isCellModel ? RoutePaths.EditCellModel : RoutePaths.EditRecipe, { id: entityId }), "_blank");
        const variantMessage = `"${payload.data.name}" Variant has been successfully added to ${payload.simulationName} Simulation.`;
        if (isVariant) {
            yield put(makeUnsaved());
            const state = yield call([backendClient, RecipeScalingApiMethods.GetRsWorkspaceTemporary], workspaceId);
            yield put(createEntityFromResultElementSourceSuccessful(state));
        }
        else {
            yield put(createEntityFromResultElementSourceSuccessful(null));
        }
        Toaster().show({
            message: isVariant ? variantMessage : regularMessage,
            intent: Intent.SUCCESS,
        });
    }
    catch (e) {
        yield call(apiErrorHandler, e, `create ${payload.entityType === EntitiesCreatableFromResultElement.CellModel ? "Cell model" : "Recipe"} from source row failed`);
    }
}
export function* updatePlotSourceConfigsSaga({ payload: { payload: { resultSetId, elementId, elementType, ranges }, callback, }, }) {
    const editMode = yield select(isEditableSelector);
    try {
        yield call(waitForLogin);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const preparedRanges = Object.keys(ranges).reduce((acc, key) => {
            const { responseId, autorange, min, max, fixedrange, showgrid } = {
                ...ranges[key],
            };
            const config = { fixedRange: !!fixedrange, autoRange: !!autorange, min, max, showGrid: showgrid };
            if (!isNotEmpty(responseId)) {
                return { ...acc, xConfig: config };
            }
            else {
                return { ...acc, sourceConfigs: { ...(acc.sourceConfigs ?? {}), [responseId]: config } };
            }
        }, {});
        if (editMode) {
            yield put(makeUnsaved());
            const backendClient = BackendClient.getInstance();
            yield call([backendClient, RecipeScalingApiMethods.UpdatePlotSourceConfigs], {
                workspaceId,
                resultSetId,
                elementId,
                elementType,
                ...preparedRanges,
            });
        }
        callback && callback(preparedRanges);
    }
    catch (e) {
        const errorMessage = `update ${elementType} plot source config failed`;
        if (editMode) {
            yield call(apiErrorHandler, e, errorMessage);
        }
        else {
            logger.error(errorMessage);
        }
        yield put(updatePlotSourceConfigsFailure());
    }
}
export function* setPlotRangeConfigsSaga({ payload: { resultSetId, elementId, ranges }, }) {
    let resultElement;
    try {
        resultElement = yield select(getResultSetElementByIdSelector(resultSetId, elementId));
        if (!resultElement) {
            return;
        }
        yield put(setPlotRangeConfigsSuccessful({
            resultSetId,
            ...resultElement,
            xPlotConfig: {
                ...(resultElement.xPlotConfig ?? {}),
                ...(ranges.xConfig ?? {}),
            },
            sources: resultElement.sources?.map((source) => ({
                ...source,
                config: {
                    ...(source.config ?? {}),
                    ...(ranges.sourceConfigs?.[source.id ?? -1] ?? {}),
                },
            })) ?? null,
        }));
    }
    catch (e) {
        logger.error(`update ${resultElement?.type} plot source config failed`);
        yield put(setPlotRangeConfigsFailure());
    }
}
